import qs from "qs";
import {
  CRM_FILTER_FIELD_CACHE_SECONDS,
  CRM_FILTER_FIELD_PAGE_SIZE,
} from "constants/crmFieldFilter";
import {
  CrmEntityLayout,
  CrmExportFormat,
  CrmExportPreviewResponse,
  CrmExportResponse,
  CrmExportTemplate,
  CrmField,
  CrmFieldValueInput,
  CrmFilterField,
  CrmFilterFieldsResponse,
  CrmLayout,
  CrmLayoutNodeInput,
  CrmSectionInstance,
  CrmTabSummary,
} from "types/Models/crmFields";
import { emptyApi } from "./emptyApi";

type PaginatedFields = {
  results: CrmField[];
  total_pages: number;
  current_page: number;
  total_count: number;
  per_page: number;
};

const extendedApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getCrmLayout: build.query<CrmLayout, { tabId?: string | null }>({
      query: ({ tabId = null }) => {
        const queryUrl = qs.stringify(
          { tab_id: tabId },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/company/crm/layout/${queryUrl}`;
      },
      providesTags: ["CRM_LAYOUT"],
    }),

    getCrmTabs: build.query<{ tabs: CrmTabSummary[] }, { entityId: string }>({
      query: ({ entityId }) => `/api/inkle/company/entity/${entityId}/crm/tab/`,
      providesTags: ["CRM_LAYOUT"],
    }),

    getEntityCrmFields: build.query<
      CrmEntityLayout,
      { entityId: string; tabId?: string | null }
    >({
      query: ({ entityId, tabId = null }) => {
        const queryUrl = qs.stringify(
          { tab_id: tabId },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/company/entity/${entityId}/crm/field/${queryUrl}`;
      },
      keepUnusedDataFor: 0,
      providesTags: ["CRM_ENTITY_FIELDS"],
    }),

    updateEntityCrmFields: build.mutation<
      { fields: CrmField[] },
      { entityId: string; values: CrmFieldValueInput[] }
    >({
      query: ({ entityId, values }) => ({
        url: `/api/inkle/company/entity/${entityId}/crm/field/`,
        method: "PUT",
        body: { values },
      }),
      invalidatesTags: ["CRM_ENTITY_FIELDS"],
    }),

    createCrmLayoutNode: build.mutation<unknown, CrmLayoutNodeInput>({
      query: (body) => ({
        url: `/api/inkle/company/crm/layout/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CRM_LAYOUT", "CRM_ENTITY_FIELDS"],
    }),

    updateCrmLayoutNode: build.mutation<unknown, CrmLayoutNodeInput>({
      query: (body) => ({
        url: `/api/inkle/company/crm/layout/`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["CRM_LAYOUT", "CRM_ENTITY_FIELDS"],
    }),

    deleteCrmLayoutNode: build.mutation<unknown, CrmLayoutNodeInput>({
      query: (body) => ({
        url: `/api/inkle/company/crm/layout/`,
        method: "DELETE",
        body,
      }),
      invalidatesTags: ["CRM_LAYOUT", "CRM_ENTITY_FIELDS"],
    }),

    addCrmSectionInstance: build.mutation<
      CrmSectionInstance,
      { entityId: string; sectionId: string }
    >({
      query: ({ entityId, sectionId }) => ({
        url: `/api/inkle/company/entity/${entityId}/crm/section/${sectionId}/instance/`,
        method: "POST",
      }),
      invalidatesTags: ["CRM_ENTITY_FIELDS"],
    }),

    removeCrmSectionInstance: build.mutation<
      unknown,
      { entityId: string; instanceId: string }
    >({
      query: ({ entityId, instanceId }) => ({
        url: `/api/inkle/company/entity/${entityId}/crm/instance/${instanceId}/`,
        method: "DELETE",
      }),
      invalidatesTags: ["CRM_ENTITY_FIELDS"],
    }),

    getCrmFieldCatalog: build.query<
      PaginatedFields,
      { search?: string | null; pageNum?: number; pageSize?: number }
    >({
      query: ({ search = null, pageNum = 1, pageSize = 100 }) => {
        const queryUrl = qs.stringify(
          { search, page_num: pageNum, page_size: pageSize },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/company/crm/field/${queryUrl}`;
      },
      providesTags: ["CRM_LAYOUT"],
    }),

    /**
     * The catalog is paginated server side, so every page is walked and the rows concatenated -
     * the filter builder needs the whole list to resolve an applied condition's field. The list
     * is static between admin layout edits, hence the long cache retention.
     */
    getCrmFilterFields: build.query<CrmFilterField[], void>({
      queryFn: async (_arg, _api, _extraOptions, fetchWithBQ) => {
        const fields: CrmFilterField[] = [];
        let pageNum = 1;
        let totalPages = 1;

        do {
          const queryUrl = qs.stringify(
            { page_num: pageNum, page_size: CRM_FILTER_FIELD_PAGE_SIZE },
            { skipNulls: true, addQueryPrefix: true }
          );
          const response = await fetchWithBQ(
            `/api/inkle/company/crm/filter/field/${queryUrl}`
          );

          if (response.error) {
            return { error: response.error };
          }

          const page = response.data as CrmFilterFieldsResponse;
          fields.push(...page.results);
          totalPages = page.total_pages;
          pageNum += 1;
        } while (pageNum <= totalPages);

        return { data: fields };
      },
      providesTags: ["CRM_LAYOUT"],
      keepUnusedDataFor: CRM_FILTER_FIELD_CACHE_SECONDS,
    }),

    getCrmExportTemplates: build.query<
      { templates: CrmExportTemplate[] },
      void
    >({
      query: () => `/api/inkle/company/crm/export/templates/`,
      providesTags: ["CRM_EXPORT_TEMPLATES"],
    }),

    saveCrmExportTemplate: build.mutation<
      CrmExportTemplate,
      { name: string; field_keys: string[] }
    >({
      query: (body) => ({
        url: `/api/inkle/company/crm/export/templates/`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["CRM_EXPORT_TEMPLATES"],
    }),

    /**
     * Backs both the preview row-count check and the real file export - same endpoint, `preview`
     * just skips building the file. `filterParams` is whatever the group list currently has
     * applied (crm_filters, search_term, tag_filter, ...) so the export matches what's on screen;
     * the caller passes it straight from the page's own URLSearchParams rather than this slice
     * re-declaring every one of those filter fields a third time.
     */
    exportCrmFields: build.mutation<
      CrmExportPreviewResponse | CrmExportResponse,
      {
        exportFields: string[];
        exportFormat: CrmExportFormat;
        preview?: boolean;
        filterParams?: Record<string, unknown>;
      }
    >({
      query: ({
        exportFields,
        exportFormat,
        preview = false,
        filterParams = {},
      }) => {
        const queryUrl = qs.stringify(
          {
            ...filterParams,
            export_fields: exportFields.join(","),
            export_format: exportFormat,
            preview,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return `/api/inkle/company/crm/export/${queryUrl}`;
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCrmLayoutQuery,
  useGetCrmTabsQuery,
  useGetEntityCrmFieldsQuery,
  useUpdateEntityCrmFieldsMutation,
  useAddCrmSectionInstanceMutation,
  useRemoveCrmSectionInstanceMutation,
  useCreateCrmLayoutNodeMutation,
  useUpdateCrmLayoutNodeMutation,
  useDeleteCrmLayoutNodeMutation,
  useGetCrmFieldCatalogQuery,
  useLazyGetCrmFieldCatalogQuery,
  useGetCrmFilterFieldsQuery,
  useGetCrmExportTemplatesQuery,
  useSaveCrmExportTemplateMutation,
  useExportCrmFieldsMutation,
} = extendedApi;
